import type { WrittenPage } from '../sections'
import { PARTNER_FORM_EN } from '../partnerForm'

/**
 * Partner Program — the page the header's Partners menu opens.
 *
 * It was an **imported** WordPress page until 2026-08-27: five paragraphs of
 * salvaged prose in which three testimonials, three partnership tiers and every
 * "Become a Partner" button had been flattened into one another by the scrape
 * (`content/imported/pages/en/partner-program.json`, still on disk as the record
 * of what the old page said). Asked for that day with four screenshots of
 * digitopia.co's own partner page: *"partner sayfası tasarımı bu şekilde"*.
 *
 * So it is a written page now, and the reference's structure is expressed in
 * this site's own section vocabulary rather than rebuilt as bespoke markup:
 * hero with the photograph beside it, the positioning paragraphs on the grey
 * band, "Why partner with us?" as three alternating argument-and-photograph
 * rows, the partner quotes, and the three tiers as cards that each carry their
 * own action. The words are the old page's, because they are the client's own
 * copy and nobody asked for new ones.
 *
 * **The contact band at the foot is deliberately NOT the reference's.** The
 * screenshots end on digitopia.co's purple "Let's Get in Touch!" panel with
 * three buttons, and the same request said to leave ours alone: *"contact us
 * tasarımı şu anki kalsın öbür sitedekini yapmaya çalışma"*. `Layout` already
 * draws this site's contact band under every document, so this page simply
 * stops after its last card and lets it.
 *
 * Two of the reference's four photographs are here as files
 * (`partner-handshake-celebration`, `impact-platform-target-state`); the other
 * two already exist in `photo-src/` in better and newer versions — the
 * presentation shot is `daimi-score-presentation.jpg`, which is the same scene
 * reshot with the 2026 DAIMI screen in it, and the applause shot is
 * `team-applause.jpg` at 2560px instead of 600.
 */
export const partnerProgram: WrittenPage = {
  navLabel: 'Partner Program',
  title: 'Partner Program',
  description:
    'Join the Digitopia Partner Program. Deliver transformation with the Impact Platform, get certified as a maturity index practitioner, and open a new revenue stream.',
  sections: [
    {
      kind: 'hero',
      eyebrow: 'Partners',
      headline: 'Grow together with Digitopia: join our Partner Program.',
      sub: 'Reinvent consulting together, and delight your customers.',
      photo: {
        src: '/photo/partner-handshake-celebration.webp',
        alt: 'Two consultants shaking hands while their colleagues applaud in a glass-walled office.',
      },
      ctas: [{ label: 'Become a Partner', href: '#contact' }],
      check:
        'The handshake photograph is the file from digitopia.co and it is only ' +
        '768×432 — the smallest source in photo-src/, and this is a hero, so the ' +
        'browser paints it at roughly its own width with nothing left for a ' +
        'retina screen. Every other hero on the site is 2560px. If the original ' +
        'shoot or the stock licence is to hand, drop a 2560px version in as ' +
        'photo-src/partner-handshake-celebration.webp and run `pnpm --filter ' +
        '@digitopia/website images`; nothing in this file changes.',
    },
    {
      kind: 'prose',
      paragraphs: [
        'Digitopia is revolutionizing the consulting industry through the Impact Platform, transformation management software used in over 20 countries. We guide companies and consultants towards better transformation processes.',
        'We believe in the power of collaboration. Our Partner Program is designed to create mutually beneficial relationships that drive success for both you and your clients. Whether you are a consulting firm, a technology provider or an industry influencer, partnering with us means joining a network dedicated to transforming the digital landscape.',
      ],
      check:
        '"Over 20 countries" and "the world\'s leading transformation management ' +
        'software" are digitopia.co\'s own wording, carried across from the ' +
        'imported page. The superlative is dropped here — the rest of this site ' +
        'does not make it — and the figure is kept. Confirm 20 is still the ' +
        'number, and whether the superlative should come back.',
    },
    // The heading that names the three rows under it. A centred standfirst gets
    // no grey band (see `background()` in ContentPage), so the reader meets the
    // question and the three answers as one white block, the way the reference
    // sets them.
    {
      kind: 'prose',
      align: 'center',
      heading: 'Why partner with us?',
      sub: 'Three things a partnership with Digitopia puts in your hands.',
    },
    {
      kind: 'split',
      flip: true,
      heading: 'Innovative Solutions',
      paragraphs: [
        'Move beyond traditional spreadsheets. The platform offers a comprehensive yet user-friendly approach to managing business transformation, and as a partner you have access to it for your own client engagements.',
      ],
      photo: {
        src: '/photo/impact-platform-target-state.webp',
        alt: 'The Impact Platform target-state screen: a maturity score, scores by dimension and an industry benchmark side by side.',
      },
      cta: { label: 'Become a Partner', href: '#contact' },
    },
    {
      kind: 'split',
      heading: 'Expertise Development',
      paragraphs: [
        'Become a certified Digital Maturity Index Practitioner through our specialized course, equipping you to offer expert transformation consultancy under your own brand.',
      ],
      photo: {
        src: '/photo/daimi-score-presentation.jpg',
        alt: 'A consultant presenting a DAIMI score and benchmark on a meeting-room screen to three colleagues.',
      },
      cta: { label: 'DAIMI course', href: '/en/digital-ai-maturity-index-daimi-course' },
      check:
        'The imported page offers two certifications — Digital Maturity Index ' +
        'Practitioner and Sustainability Maturity Index Practitioner. Only the ' +
        'first is named here, because this site\'s indices are DAIMI and AIMI and ' +
        'there is no Sustainability Maturity Index anywhere else on it. The ' +
        'button already opens the Digital & AI Maturity Index Course page, so the ' +
        'paragraph says DMI while the link says DAIMI. Confirm which ' +
        'certifications the course awards today and which name this paragraph ' +
        'should use.',
    },
    {
      kind: 'split',
      flip: true,
      heading: 'Competitive Advantage & Revenue Growth',
      paragraphs: [
        'Stand out in your industry by offering transformation management software trusted by organizations worldwide, and unlock new revenue streams through flexible partnership models designed to maximize your growth potential.',
      ],
      photo: {
        src: '/photo/team-applause.jpg',
        alt: 'A project team around a boardroom table applauding in front of a whiteboard roadmap.',
      },
      cta: { label: 'Become a Partner', href: '#contact' },
    },
    // The three partner quotes, in the Hall of Fame card with a tab per company
    // — asked for on 2026-08-28 with a screenshot of digitopia.co's own version
    // of this section and the three marks and three portraits: *"partner
    // sayfasındaki testimonial görselleri hall of fame tasarımının aynısı
    // örnektekigibi kaydırmalı üç seçenek olacak"*. The words are unchanged; the
    // order is the reference's, which is the order they were given in.
    //
    // `company` is both the card's eyebrow and the tab's label, so it is the
    // company as it should be *written* — the reference shortens the middle one
    // to "MSS BTA" in its tab strip and we do not, because the mark under it
    // spells the full name out and a site-wide abbreviation nobody else uses is
    // a second name for the same partner.
    {
      kind: 'quotes',
      variant: 'fame',
      heading: 'What our partners say.',
      items: [
        {
          quote:
            'AI is not just a technology shift, it is a leadership challenge. With AIMI, we can give our clients the structure and visibility they need to turn AI potential into business value.',
          source: 'Russ Ballard, Partner, Fisher Management Partners',
          company: 'Fisher Management Partners',
          logo: { name: 'Fisher Management Partners', src: '/logo/fishermanagementpartners.webp' },
          portrait: { src: '/photo/portrait-russ-ballard.webp', alt: 'Russ Ballard' },
        },
        {
          quote:
            'Digitopia’s Impact Platform helps us deliver greater value to our clients by turning complexity into clarity. With structured maturity assessments and clear roadmaps, we enable leaders to prioritise what matters most, align their teams, and achieve measurable transformation outcomes.',
          source: 'Peter Meyers, CMO, MSS Business Transformation Advisory',
          company: 'MSS Business Transformation Advisory',
          logo: { name: 'MSS Business Transformation Advisory', src: '/logo/mssbta.webp' },
          portrait: { src: '/photo/portrait-peter-meyers.webp', alt: 'Peter Meyers' },
        },
        {
          quote:
            'Our partnership with Digitopia has been transformative for NexGen. Impact Platform has become a cornerstone of our service: it integrates all stakeholders, is automated and effective, simplifies complex processes, and aligns with market needs.',
          source: 'Jose Antonio De La Ossa Duarte, CEO, NexGen',
          company: 'NexGen',
          logo: { name: 'NexGen', src: '/logo/nexgen.webp' },
          portrait: {
            src: '/photo/portrait-jose-antonio-de-la-ossa-duarte.webp',
            alt: 'Jose Antonio De La Ossa Duarte',
          },
        },
      ],
    },
    {
      kind: 'cards',
      heading: 'Become a Partner',
      sub: 'Three ways to work with us. Tell us which one you are and we will take it from there.',
      // Asked for 2026-08-29: the blue FOR INDEPENDENT CONSULTANTS / FOR
      // RESELLERS / FOR PLATFORM BUILDERS labels go, and the three CTAs
      // become real buttons rather than arrowed text links. The card titles
      // already say who each one is for, so the kicker only added a second
      // blue line above them.
      buttonCtas: true,
      ctaAlignLeft: true,
      items: [
        {
          title: 'Consulting Partners',
          promise:
            'This partnership program is specially designed for freelance consultants and boutique consulting firms.',
          points: [],
          cta: { label: 'Become a Partner', href: '#contact' },
        },
        {
          title: 'Solution Partners',
          promise:
            'Reselling Digitopia’s solutions is good business, and it augments the business you already have.',
          points: [],
          cta: { label: 'Become a Partner', href: '#contact' },
        },
        {
          title: 'Platform Partners',
          promise:
            'You want to reinvent consulting on our platform. Then we should definitely talk.',
          points: [],
          cta: { label: 'Become a Partner', href: '#contact' },
        },
      ],
    },
    /*
      The page's own form, and the `#contact` every "Become a Partner" button on
      it scrolls to. It replaces the shared closing band here (see
      `NO_CONTACT_BAND` in `app/Layout.tsx`) rather than sitting beside it,
      because two forms and two `id="contact"` on one document is neither.

      The fields are the band's plus the two this page needs — see
      `content/partnerForm.ts`.
    */
    {
      kind: 'form',
      // The band's own heading, because this section stands in its place — and
      // not the cards' "Become a Partner" three inches above it.
      heading: "Let's Get in Touch!",
      ...PARTNER_FORM_EN,
    },
  ],
}
